import { ListFilter } from 'lucide-preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { getHeaderFilterDisplayConfig } from '../../content';
import { useFilter } from '../../contexts/FilterContext';
import type { UserInfo } from '../../functions/auth/authService';
import type { AppRoute, HeaderFilterId } from '../../routing';
import type { Client, ClientUnit } from '../../types/api';
import { CurrencyFilter } from './CurrencyFilter';
import { PeriodFilter } from './PeriodFilter';
import { HeaderFilterSelect, type HeaderFilterOption } from './HeaderFilterSelect';

interface GlobalFilterProps {
  user: UserInfo;
  currentPage: AppRoute;
}

function getClientLabel(client: Client): string {
  return client.name || client.client_email || `Client ${client.user_id_no}`;
}

function getUnitLabel(unit: ClientUnit): string {
  return `${unit.country || 'Unknown'} - Unit ${unit.unit_id_no}`;
}

export const GlobalFilter = ({ user, currentPage }: GlobalFilterProps) => {
  const {
    selectedClient,
    selectedUnit,
    clients,
    clientUnits,
    loading,
    error,
    setClient,
    setUnit,
  } = useFilter();
  const [isExpanded, setIsExpanded] = useState(false);
  const [areFiltersMounted, setAreFiltersMounted] = useState(false);
  const collapseTimerRef = useRef<number>();
  const filterConfig = getHeaderFilterDisplayConfig(currentPage);
  const canShowFilter = (filterId: HeaderFilterId) =>
    filterId !== 'client' || user.is_admin;
  const pinnedFilters = filterConfig.pinned.filter(canShowFilter);
  const hiddenFilters = filterConfig.hidden.filter(canShowFilter);
  const hasFilters = pinnedFilters.length > 0 || hiddenFilters.length > 0;

  useEffect(() => {
    window.clearTimeout(collapseTimerRef.current);
    setIsExpanded(false);
    setAreFiltersMounted(false);
  }, [currentPage]);

  useEffect(
    () => () => {
      window.clearTimeout(collapseTimerRef.current);
    },
    [],
  );

  const clientOptions = useMemo<HeaderFilterOption<Client>[]>(
    () =>
      clients.map((client) => ({
        key: String(client.user_id_no),
        label: getClientLabel(client),
        searchText: [client.name, client.client_email, client.user_id_no].filter(Boolean).join(' '),
        value: client,
      })),
    [clients],
  );

  const unitOptions = useMemo<HeaderFilterOption<ClientUnit>[]>(
    () =>
      clientUnits.map((unit) => ({
        key: String(unit.unit_id_no),
        label: getUnitLabel(unit),
        searchText: [unit.country, unit.unit_id_no].filter(Boolean).join(' '),
        value: unit,
      })),
    [clientUnits],
  );

  if (!hasFilters) return null;

  const renderFilter = (filterId: HeaderFilterId) => {
    if (filterId === 'client') {
      return (
        <HeaderFilterSelect
          key="client"
          ariaLabel="Client"
          valueLabel={selectedClient ? getClientLabel(selectedClient) : 'Client'}
          options={clientOptions}
          selectedKey={selectedClient ? String(selectedClient.user_id_no) : undefined}
          disabled={loading}
          searchable
          onSelect={setClient}
        />
      );
    }

    if (filterId === 'currency') {
      return <CurrencyFilter key="currency" />;
    }

    if (filterId === 'period') {
      return <PeriodFilter key="period" />;
    }

    if (filterId === 'unit') {
      return (
        <HeaderFilterSelect
          key="unit"
          ariaLabel="Unit"
          valueLabel={selectedUnit ? getUnitLabel(selectedUnit) : 'Unit'}
          options={unitOptions}
          selectedKey={selectedUnit ? String(selectedUnit.unit_id_no) : undefined}
          disabled={loading}
          searchable={clientUnits.length > 8}
          onSelect={setUnit}
        />
      );
    }

    return null;
  };

  const toggleFilters = () => {
    window.clearTimeout(collapseTimerRef.current);

    if (isExpanded) {
      setIsExpanded(false);
      collapseTimerRef.current = window.setTimeout(() => {
        setAreFiltersMounted(false);
      }, 260);
      return;
    }

    setAreFiltersMounted(true);
    setIsExpanded(true);
  };

  return (
    <div className={`header-filter-group${isExpanded ? ' is-expanded' : ''}`}>
      {hiddenFilters.length > 0 && (
        <button
          type="button"
          className={`filter-trigger${isExpanded ? ' is-expanded' : ''}${error ? ' has-error' : ''}`}
          onClick={toggleFilters}
          aria-label={isExpanded ? 'Collapse page filters' : 'Expand page filters'}
          aria-expanded={isExpanded}
          title={error || (isExpanded ? 'Collapse filters' : 'Expand filters')}
        >
          <ListFilter size={17} strokeWidth={2} aria-hidden="true" />
        </button>
      )}

      {hiddenFilters.length > 0 && areFiltersMounted && (
        <div
          className={`header-filter-group__controls header-filter-group__controls--${hiddenFilters.length}${
            isExpanded ? '' : ' is-collapsing'
          }`}
        >
          {hiddenFilters.map(renderFilter)}
        </div>
      )}

      {pinnedFilters.map(renderFilter)}
    </div>
  );
};
